import React from 'react';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import CenteredSpinner from '../../loading-indicators/centered-spinner';
import { useSelector } from 'react-redux';
import Presentation from '../../../logic/redux/presentation';
import { ScreenSize } from '../../../styles/screen-sizes';

interface ListItemProps<ListItemType> {
  listItem: ListItemType;
  mobileHeight: number;
  onClick?: (item: ListItemType) => void;
}

interface InfinitListProps<ListItemType> {
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  items: readonly ListItemType[];
  desktopItemHeight: number;
  mobileItemHeight: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadNextPage: (startIndex: number, stopIndex: number) => Promise<any> | null;
  listItem: React.FunctionComponent<ListItemProps<ListItemType>>;
  onClickItem?: (item: ListItemType) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InfiniteList = <ListItemType extends any>(
  props: InfinitListProps<ListItemType>,
): JSX.Element => {
  const screenSize = useSelector(Presentation.select.selectScreenSize);
  const itemHeight = screenSize <= ScreenSize.SM ? props.mobileItemHeight : props.desktopItemHeight;

  const {
    hasNextPage,
    isNextPageLoading,
    items,
    loadNextPage,
    mobileItemHeight,
    listItem,
    onClickItem,
  } = props;
  const itemCount = hasNextPage ? items.length + 1 : items.length;
  const loadMoreItems = isNextPageLoading
    ? () => {
        return null;
      }
    : loadNextPage;

  const isItemLoaded = (index: number): boolean => !hasNextPage || index < items.length;

  const Item = (props: ListChildComponentProps): JSX.Element => {
    // eslint-disable-next-line react/prop-types
    const { index, style } = props;

    if (!isItemLoaded(index)) {
      return <CenteredSpinner style={style} />;
    } else {
      return (
        <div style={style}>
          {listItem({
            listItem: items[index],
            mobileHeight: mobileItemHeight,
            onClick: onClickItem,
          })}
        </div>
      );
    }
  };

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={loadMoreItems}
      threshold={1}
      minimumBatchSize={10}
    >
      {({ onItemsRendered, ref }) => (
        <List
          className="List"
          height={window.innerHeight - 240}
          itemCount={itemCount}
          itemSize={itemHeight + 10}
          onItemsRendered={onItemsRendered}
          ref={ref}
          width={'100%'}
        >
          {Item}
        </List>
      )}
    </InfiniteLoader>
  );
};

export default InfiniteList;
